/**
 * Error Handler Utility
 *
 * Centralized, null-safe error message extraction with domain awareness.
 * Handles all error types: network errors, HTTP errors, axios errors, unknown errors.
 * Never crashes - always returns user-friendly fallback messages.
 */

import { captureSentryError } from "/@/plugins/sentry";


/**
 * Structured upload error result compatible with Create.vue template
 */
export interface UploadErrorResult {
  message: string;
  errors: Array<{ document_type: string; exception_messages: string }>;
  isFileValidation: boolean;
}

/**
 * User-friendly network error messages
 */
const NETWORK_ERROR_MESSAGES = {
  timeout: 'Request timed out. Please check your connection and try again.',
  networkError: 'Network error. Please check your internet connection.',
  cors: 'Connection error. Please try again or contact support.',
  serverDown: 'Unable to reach server. Please try again later.',
  unknown: 'An unexpected error occurred. Please try again.'
};


/**
 * HTTP status code to user message mapping
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your information and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'Access denied. You may not have permission for this action.',
  404: 'Resource not found.',
  422: 'Validation failed. Please check your information.',
  500: 'Server error occurred. Please try again in a moment.',
  502: 'Service temporarily unavailable. Please try again shortly.',
  503: 'Service maintenance in progress. Please try again later.',
  504: 'Request timed out. Please try again.'
};

/**
 * Extract backend error message from response data
 * Handles different backend response formats (description, message, msg, error)
 */
function extractBackendMessage(error: any): string | null {
  if (!error?.response?.data) return null;

  const data = error.response.data;

  // Try all known backend response formats
  return data.description || data.message || data.msg || data.error || null;
}

/**
 * Check if error is a network error (no response from server)
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const axiosError = error as any;
  return !axiosError.response && axiosError.message === 'Network Error';
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const axiosError = error as any;
  return axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout');
}

/**
 * Check if error is a CORS error
 */
function isCorsError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const axiosError = error as any;
  return !axiosError.response && axiosError.message?.toLowerCase().includes('cors');
}

/**
 * Check if error is file validation error
 */
export function isFileValidationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const axiosError = error as any;
  const message = extractBackendMessage(axiosError)?.toLowerCase() || '';
  return message.includes('file') && (
    message.includes('size') ||
    message.includes('type') ||
    message.includes('format') ||
    message.includes('invalid')
  );
}

/**
 * Main error message extraction function
 * @param error - The error object (can be anything)
 * @returns User-friendly error message (never returns undefined)
 */
export function extractErrorMessage(error: unknown): string {
  // Handle null/undefined
  if (!error) {
    return NETWORK_ERROR_MESSAGES.unknown;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;  // String is already validated as truthy by typeof check
  }

  // Handle non-object errors
  if (typeof error !== 'object') {
    return NETWORK_ERROR_MESSAGES.unknown;
  }

  const axiosError = error as any;

  // Capture to Sentry (will be sanitized by beforeSend hook)
  captureSentryError(error, {
    errorType: !axiosError.response ? 'network' : 'http',
    status: axiosError.response?.status,
  });

  // Check for network errors first (no response)
  if (!axiosError.response) {
    if (isTimeoutError(error)) {
      return NETWORK_ERROR_MESSAGES.timeout;
    }
    if (isCorsError(error)) {
      return NETWORK_ERROR_MESSAGES.cors;
    }
    if (isNetworkError(error)) {
      return NETWORK_ERROR_MESSAGES.networkError;
    }
    // Generic network error
    return axiosError.message || NETWORK_ERROR_MESSAGES.serverDown;
  }

  // Try to extract backend message first
  const backendMessage = extractBackendMessage(axiosError);
  if (backendMessage) {
    return backendMessage;
  }

  // Fall back to HTTP status code message
  const status = axiosError.response?.status;
  if (status && HTTP_ERROR_MESSAGES[status]) {
    return HTTP_ERROR_MESSAGES[status];
  }

  // Last resort - generic error
  return axiosError.message || NETWORK_ERROR_MESSAGES.unknown;
}

/**
 * Extract upload-specific error with structured format
 * Returns format compatible with Create.vue template expectations
 */
export function extractUploadError(error: unknown): UploadErrorResult {
  const message = extractErrorMessage(error);
  const isFileValidation = isFileValidationError(error);

  // Check if backend returned structured validation errors
  if (error && typeof error === 'object') {
    const axiosError = error as any;
    const responseData = axiosError.response?.data;

    // If backend provides structured errors, use them
    if (responseData && typeof responseData === 'object') {
      // Check for array of errors (backend validation response)
      if (Array.isArray(responseData)) {
        return {
          message,
          errors: responseData.map((err: any) => ({
            document_type: err.document_type || err.documentType || 'Unknown',
            exception_messages: err.exception_messages || err.message || 'Upload failed'
          })),
          isFileValidation
        };
      }

      // Check for errors property
      if (responseData.errors && Array.isArray(responseData.errors)) {
        return {
          message,
          errors: responseData.errors.map((err: any) => ({
            document_type: err.document_type || err.documentType || 'Unknown',
            exception_messages: err.exception_messages || err.message || 'Upload failed'
          })),
          isFileValidation
        };
      }
    }
  }

  // Return single error in array format for template compatibility
  return {
    message,
    errors: [{
      document_type: 'Upload',
      exception_messages: message
    }],
    isFileValidation
  };
}

